import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import styles from './legal.module.css';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    document.body.style.overflow = 'auto';
    return () => { if (root) { root.style.overflow = ''; root.style.height = ''; } document.body.style.overflow = ''; };
  }, []);

  return (
    <div className={styles.container}>
      <>
        <title>Privacy Policy | Tasking.tech</title>
        <meta
          name="description"
          content="Privacy Policy for Tasking.tech - AI-Powered Secure Code Collaboration Platform"
        />
      </>

      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navContent}>
          <Link to="/" className={styles.logo}>
            <img
              src="/logo.png"
              alt="Tasking.tech"
              className={styles.logoIcon}
            />
            <span className={styles.logoText}>
              <span>tasking</span>
              <span className={styles.techSuffix}>.tech</span>
            </span>
          </Link>
          <Button variant="ghost" asChild>
            <Link to="/">
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </Button>
        </div>
      </nav>

      <main className={styles.content}>
        <h1>Privacy Policy</h1>
        <p className={styles.lastUpdated}>Last updated: February 1, 2025</p>

        <section className={styles.section}>
          <h2>1. Introduction</h2>
          <p>
            Tasking.tech ("we", "our", or "us") is committed to protecting your privacy. This
            Privacy Policy explains how we collect, use, disclose, and safeguard your information
            when you use our AI-powered code collaboration platform.
          </p>
        </section>

        <section className={styles.section}>
          <h2>2. Information We Collect</h2>
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li>Account information (email, name, profile picture)</li>
            <li>Code and files you upload or create</li>
            <li>Messages and communications within the platform</li>
            <li>Payment information (processed securely by our payment providers)</li>
          </ul>

          <h3>2.2 Information Collected Automatically</h3>
          <ul>
            <li>Usage data and analytics</li>
            <li>Device information and browser type</li>
            <li>IP address and location data</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information to:</p>
          <ul>
            <li>Provide and maintain the Service</li>
            <li>Process transactions and send related information</li>
            <li>Improve our AI features and code assistance</li>
            <li>Send you technical notices and support messages</li>
            <li>Respond to your comments, questions, and requests</li>
            <li>Monitor and analyze usage patterns and trends</li>
            <li>Detect and prevent fraudulent or unauthorized activity</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>4. AI and Code Processing</h2>
          <p>
            Our AI features process your code to provide suggestions and assistance. We want you to
            understand that:
          </p>
          <ul>
            <li>Your code is processed to generate contextual AI responses</li>
            <li>We do not use your private code to train our AI models without consent</li>
            <li>AI processing occurs in secure, isolated environments</li>
            <li>You maintain ownership of all code you create</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>5. Data Sharing and Disclosure</h2>
          <p>We may share your information with:</p>
          <ul>
            <li>
              <strong>Service Providers:</strong> Third parties who help us operate the Service
            </li>
            <li>
              <strong>Collaborators:</strong> Users you invite to your sessions (only what you
              explicitly share)
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to protect our rights
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale
              of assets
            </li>
          </ul>
          <p>We do not sell your personal information to third parties.</p>
        </section>

        <section className={styles.section}>
          <h2>6. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your information, including:
          </p>
          <ul>
            <li>End-to-end encryption for sensitive data</li>
            <li>Secure, sandboxed execution environments</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Access controls and authentication requirements</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>7. Data Retention</h2>
          <p>
            We retain your information for as long as your account is active or as needed to provide
            services. You may request deletion of your data at any time by contacting us.
          </p>
        </section>

        <section className={styles.section}>
          <h2>8. Your Rights</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to or restrict processing of your data</li>
            <li>Data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>9. Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to enhance your experience. You can control
            cookie preferences through your browser settings. Essential cookies are required for the
            Service to function properly.
          </p>
        </section>

        <section className={styles.section}>
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own.
            We ensure appropriate safeguards are in place for international transfers in compliance
            with applicable data protection laws.
          </p>
        </section>

        <section className={styles.section}>
          <h2>11. Children's Privacy</h2>
          <p>
            The Service is not intended for users under 13 years of age. We do not knowingly collect
            personal information from children under 13.
          </p>
        </section>

        <section className={styles.section}>
          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes
            by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section className={styles.section}>
          <h2>13. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please
            contact us at:
          </p>
          <p>
            <a href="mailto:privacy@tasking.tech">privacy@tasking.tech</a>
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <img
              src="/logo.png"
              alt="Tasking.tech"
              className={styles.footerLogo}
            />
            <span>
              tasking<span className={styles.techSuffix}>.tech</span>
            </span>
          </div>
          <div className={styles.footerLinks}>
            <Link to="/docs">Docs</Link>
            <Link to="/security">Security</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/">Home</Link>
          </div>
          <span className={styles.copyright}>© 2026 Tasking.tech. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
